import type { ActorIdentity } from "@/components/app/entities/actor-hover-view";
import type {
  CurrentMlxUser,
  WorkspaceFeedItem,
  WorkspaceOrganizationMember,
  WorkspaceSession,
} from "@/lib/mlx-workspace-client";

export type ActorDisplay = {
  name: string;
  initials: string;
  imageUrl: string | null;
  icon?: "twin" | "key";
  /** Who is behind the name, for their card; a connector has none. */
  identity?: ActorIdentity;
};
export type FeedMemberDisplay = Pick<
  WorkspaceOrganizationMember,
  "name" | "email" | "imageUrl"
> & Partial<Pick<WorkspaceOrganizationMember, "userId">>;

export function resolveFeedActor(
  actor: WorkspaceFeedItem["actor"],
  membersById: Map<string, FeedMemberDisplay>,
  selfIds: Set<string>,
  currentUser: CurrentMlxUser | null,
): ActorDisplay {
  const ids = [actor.subjectId, actor.id].filter((id): id is string =>
    Boolean(id?.trim()),
  );
  const member = ids
    .map((id) => membersById.get(id) ?? membersById.get(id.toLocaleLowerCase()))
    .find(Boolean);
  if (
    ids.some(
      (id) =>
        selfIds.has(id) ||
        id.toLocaleLowerCase() === currentUser?.email?.toLocaleLowerCase(),
    )
  ) {
    const imageUrl = member?.imageUrl ?? currentUser?.imageUrl ?? null;
    return {
      name: "You",
      initials: "You",
      imageUrl,
      identity: { kind: "person", userId: member?.userId ?? currentUser?.id ?? ids[0] ?? null, name: currentUser?.name?.trim() || "You", imageUrl },
    };
  }
  const name = member?.name?.trim() || member?.email?.trim();
  if (name)
    return {
      name,
      initials: name
        .split(/[\s@._-]+/u)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join(""),
      imageUrl: member?.imageUrl ?? null,
      identity: { kind: "person", userId: member?.userId ?? ids[0] ?? null, name, imageUrl: member?.imageUrl ?? null },
    };
  if (actor.type === "agent")
    return { name: "Agent", initials: "AI", imageUrl: null, identity: { kind: "twin" } };
  if (actor.type === "service" && actor.apiKeyLabel?.trim())
    return {
      name: `${actor.apiKeyLabel.trim()} (API key)`,
      initials: "API",
      imageUrl: null,
      icon: "key",
      identity: { kind: "apiKey", label: actor.apiKeyLabel.trim() },
    };
  if (actor.type === "service")
    return { name: "Connector", initials: "⇄", imageUrl: null };
  if (actor.type === "user")
    // Nobody the directory knows: no card, rather than a card about no one.
    return { name: "Unknown person", initials: "?", imageUrl: null };
  return { name: "Twin", initials: "T", imageUrl: null, icon: "twin", identity: { kind: "twin" } };
}

export function sessionFeedActor(
  session: WorkspaceSession,
  currentUser: CurrentMlxUser | null,
  members: Map<string, FeedMemberDisplay>,
): ActorDisplay {
  const ids = [
    session.pluginSubjectId,
    session.verifiedSubjectId,
    session.userId,
  ].filter((id): id is string => Boolean(id));
  const selfIds = new Set(currentUser ? [currentUser.id] : []);
  const identity =
    ids.find(
      (id) =>
        selfIds.has(id) ||
        members.has(id) ||
        members.has(id.toLocaleLowerCase()),
    ) ?? ids[0];
  if (!identity) return { name: "Twin", initials: "T", imageUrl: null };
  return resolveFeedActor(
    {
      id: identity,
      subjectId: null,
      type: "user",
      clientApp: null,
      interfaceType: null,
      executedByTwin: false,
    },
    members,
    selfIds,
    currentUser,
  );
}
